import { useEffect, useState } from "react";
import { useRequestFinished } from "../../../common/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
} from "../../../../store/slices/rolesSlice";
import RoleDetailsForm from "./RoleDetailsForm";
import { connect } from "react-redux";
import { AccessScopeConstants } from "../../../../constants";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import TooltipButton from "../../../common/tooltipButton";

export const RoleDetails = ({
  action,
  createRequest,
  detailsRequest,
  error,
  labels,
  menu,
  record,
  requesting,
  user,
  updateDetailRequest,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const params = useParams();
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.UpdateScope
  );

  // Only a save answered while this screen is open: the persisted store can
  // still say "update" from an earlier save when the screen mounts (F-BUG-12).
  const requestFinished = useRequestFinished(requesting);
  useEffect(() => {
    if ((action === "create" || action === "update") && requestFinished && !error) {
      navigate(menu.to);
    }
  }, [error, action, requestFinished, menu, navigate]);

  useEffect(() => {
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
  }, [params?.id, detailsRequest]);

  const onSubmit = (values) => {
    let payload = {
      name: values.name,
      scope: {
        UserAccess: "",
        ContentAccess: "",
        ReportAccess: "",
        EnterpriseAccess: "",
        AppAccess: "",
      },
    };
    Object.keys(values.scopes).forEach((key) => {
      if (values.scopes[key]) {
        Object.keys(values.scopes[key]).forEach((scopeKey) => {
          if (values.scopes[key][scopeKey]) {
            payload.scope[key] += `${scopeKey}`;
          }
        });
        if (payload.scope[key] && !payload.scope[key].includes("Read")) {
          payload.scope[key] = `Read${payload.scope[key]}`;
        }
      }
    });
    if (params?.id) {
      updateDetailRequest({ id: record.id, data: payload });
    } else {
      createRequest(payload);
    }
  };

  return (
    <div>
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: params?.id ? "edit_role" : "add_role" })}
          </div>
          {(params?.id && !isEditable && (
            <div className="edexlms-page-actions">
              <TooltipButton
                icon="pi pi-pencil"
                outlined
                onClick={() =>
                  (record?.id && setIsEditable(!isEditable)) || null
                }
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate || record?.isSystemDefined}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({
                        id:
                          (!record?.isSystemDefined && "no_permission") ||
                          "no_system_defined_permission_role",
                      })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          <Form
            initialValues={{
              name: record?.name || "",
              scopes: {
                UserAccess: {
                  // Create, Update and Delete inherit Read: a role saved
                  // with a write but no stored "Read" still shows Read
                  // ticked (RoleHelper.checkRoleScope, the same check
                  // menus and route gating use).
                  Read: RoleHelper.checkRoleScope(
                    record?.scope,
                    AccessScopeConstants.UserAccess,
                    AccessScopeConstants.ReadScope
                  ),
                  Create:
                    record?.scope?.UserAccess?.includes("Create") || false,
                  Update:
                    record?.scope?.UserAccess?.includes("Update") || false,
                  Delete:
                    record?.scope?.UserAccess?.includes("Delete") || false,
                },
                ContentAccess: {
                  Read: RoleHelper.checkRoleScope(
                    record?.scope,
                    AccessScopeConstants.ContentAccess,
                    AccessScopeConstants.ReadScope
                  ),
                  Create:
                    record?.scope?.ContentAccess?.includes("Create") || false,
                  Update:
                    record?.scope?.ContentAccess?.includes("Update") || false,
                  Delete:
                    record?.scope?.ContentAccess?.includes("Delete") || false,
                },
                ReportAccess: {
                  Read: RoleHelper.checkRoleScope(
                    record?.scope,
                    AccessScopeConstants.ReportAccess,
                    AccessScopeConstants.ReadScope
                  ),
                  Create:
                    record?.scope?.ReportAccess?.includes("Create") || false,
                  Update:
                    record?.scope?.ReportAccess?.includes("Update") || false,
                  Delete:
                    record?.scope?.ReportAccess?.includes("Delete") || false,
                },
                EnterpriseAccess: {
                  Read: RoleHelper.checkRoleScope(
                    record?.scope,
                    AccessScopeConstants.EnterpriseAccess,
                    AccessScopeConstants.ReadScope
                  ),
                  Create:
                    record?.scope?.EnterpriseAccess?.includes("Create") ||
                    false,
                  Update:
                    record?.scope?.EnterpriseAccess?.includes("Update") ||
                    false,
                  Delete:
                    record?.scope?.EnterpriseAccess?.includes("Delete") ||
                    false,
                },
              },
            }}
            disabled={!isEditable}
            requesting={requesting}
            submitBtn={{
              label: intl.formatMessage({
                id: params?.id ? "update_role" : "add_role",
              }),
              onClick: onSubmit,
            }}
            cancelBtn={{
              label: intl.formatMessage({ id: "cancel" }),
              onClick: () => navigate(menu.to),
            }}
          >
            <RoleDetailsForm disabled={!isEditable} />
          </Form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  requesting: state.roles.requesting,
  action: state.roles.action,
  error: state.roles.error,
  record: state.roles.record,
  user: state.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  createRequest: (payload) => dispatch(createRequest(payload)),
  detailsRequest: (payload) => dispatch(detailsRequest(payload)),
  updateDetailRequest: (payload) => dispatch(updateDetailRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleDetails);
